import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", code: "", spoc: "", spoc_contact: "", spoc_whatsapp: "", emails: "", address: "", gst_no: "", username: "", password: "" };

const F = [
  { key: "name", label: "Client Name", required: true },
  { key: "code", label: "Client Code" },
  { key: "spoc", label: "Client Spoc" },
  { key: "spoc_contact", label: "Spoc Contact No" },
  { key: "spoc_whatsapp", label: "Spoc WhatsApp No" },
  { key: "gst_no", label: "Client GST No." },
  { key: "username", label: "Client Username (login email)" },
  { key: "password", label: "Client Password", type: "password" },
];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, emails: (c.emails || []).join(", "), password: "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, emails: form.emails.split(",").map((x) => x.trim()).filter(Boolean) };
    if (!payload.password) delete payload.password;
    if (!payload.username) delete payload.username;
    try {
      if (editing) await api.patch(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client created");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? Their login account will be deactivated.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Clients</h1>
          <p className="text-sm text-[#4B5563] mt-1">Client master records. Adding a username + password creates a client login that sees only their own cases.</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-client-button">
          <Plus className="h-4 w-4 mr-2" /> Add Client
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="clients-table">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              {["Client Name", "Code", "Spoc", "Spoc Contact", "WhatsApp", "Email IDs", "GST No.", "Login", "Actions"].map((h) => (
                <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={9} className="py-10 text-center text-[#9CA3AF]" data-testid="clients-empty">No clients yet. Add your first client.</td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors" data-testid={`client-row-${c.id}`}>
                <td className="py-2 px-3 font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-[#9CA3AF]" />{c.name}</td>
                <td className="py-2 px-3 font-mono text-xs">{c.code || "—"}</td>
                <td className="py-2 px-3">{c.spoc || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs">{c.spoc_contact || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs">{c.spoc_whatsapp || "—"}</td>
                <td className="py-2 px-3 text-xs max-w-[220px] truncate" title={(c.emails || []).join(", ")}>{(c.emails || []).length ? c.emails.join(", ") : "—"}</td>
                <td className="py-2 px-3 font-mono text-xs">{c.gst_no || "—"}</td>
                <td className="py-2 px-3 text-xs">{c.username || "—"}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(c)} className="text-[#9CA3AF] hover:text-[#2563EB] transition-colors" data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(c)} className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors" data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="client-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading font-extrabold">{editing ? `Edit — ${editing.name}` : "Add Client"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {F.map((f) => (
              <div key={f.key}>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">{f.label}{f.required && " *"}</Label>
                <Input type={f.type || "text"} required={!!f.required} value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1 rounded-sm" data-testid={`client-form-${f.key}`} />
              </div>
            ))}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">Client Email IDs (comma separated)</Label>
              <Input value={form.emails} onChange={(e) => setForm({ ...form, emails: e.target.value })}
                className="mt-1 rounded-sm" placeholder="ops@client.com, audit@client.com" data-testid="client-form-emails" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">Client Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={1} className="mt-1 rounded-sm" data-testid="client-form-address" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="client-save-button">
                {editing ? "Save Changes" : "Create Client"}
              </Button>
              {editing && <p className="text-[10px] text-[#9CA3AF] mt-2">Leave password blank to keep the existing one. Entering a new password updates the client login.</p>}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
